import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MainMenu from '../Navigation/MainMenu/MainMenu';
import useMetricsNodeHeight from './useMetricsNodeHeight';
import CampaignMetrics from '../CampaignMetrics/CampaignMetrics';
import HeroBackground from './HeroBackground';
import HeroOverlay from './HeroOverlay';
import styles from './Header.module.scss';
import {
    useNavMenuConfig,
    useHeroConfig,
    useKeyVisualConfig,
    useStandardKeyVisualConfig,
    useCountdownConfig,
    useMetricsConfig,
    useDesignStore,
    formatCSSValue,
} from '../../stores/index.js';

/** Hero variants the header can render: the configured hero, a fixed standard key visual, or nothing. */
export const HERO_VARIANT = {
    HERO: 'hero',
    STANDARD: 'standard',
    HIDDEN: 'hidden',
};

/** Where the campaign metrics sit relative to the hero. */
export const METRICS_PLACEMENT = {
    INFLOW: 'inflow',
    MOBILE_ONLY: 'mobile',
    NONE: 'none',
};

/**
 * Default hero policy: always render the configured hero together with its overlay elements.
 * Campaigns whose subpages show a fixed standard key visual (or that hide the hero per page
 * section) inject their own policy instead of branching on the product inside the header.
 */
const defaultResolveHeroVariant = () => HERO_VARIANT.HERO;

/**
 * Default metrics placement: metrics flow into the page unless they are detached from the hero.
 * On 404 pages only the mobile block renders, because the detached overlay has no hero to sit on.
 */
const defaultResolveMetricsPlacement = ({ pageNotFound, detachedDesktop, detachedMobile, heroHidden }) => {
    if (heroHidden) return METRICS_PLACEMENT.NONE;
    if (!pageNotFound && !detachedDesktop) return METRICS_PLACEMENT.INFLOW;
    if (!detachedMobile) return METRICS_PLACEMENT.MOBILE_ONLY;
    return METRICS_PLACEMENT.NONE;
};

const Header = ({
    primaryNavigation,
    primaryNavigationCTA,
    pageNotFound,
    isFocusModeDesktop,
    isFocusModeMobile,
    counter = null,
    resolveHeroVariant = defaultResolveHeroVariant,
    resolveMetricsPlacement = defaultResolveMetricsPlacement,
    heroOverlayPadding,
}) => {
    const [groupHeights, setGroupHeights] = useState({});

    const onGroupHeightChange = useCallback((id, height) => {
        setGroupHeights((prev) => {
            if (prev[id] === height) return prev;
            const updated = { ...prev };
            if (height === 0) {
                delete updated[id];
            } else {
                updated[id] = height;
            }
            return updated;
        });
    }, []);

    const maxOverlayHeight = useMemo(() => {
        const heights = Object.values(groupHeights);
        if (heights.length === 0) return 0;
        const max = Math.max(...heights);
        return max > 0 ? max + 32 : 0; // 32px padding buffer (16px top/bottom)
    }, [groupHeights]);

    const { isFullWidth: isNavFullWidth, backgroundColor: navBackgroundColor } = useNavMenuConfig();
    const { isFullWidth: isKeyVisualFullWidth } = useKeyVisualConfig();
    const { isFullWidth: isStandardKeyVisualFullWidth } = useStandardKeyVisualConfig();
    const { hasHero } = useHeroConfig();
    const { isFullWidth: isCountdownFullWidth } = useCountdownConfig();
    const {
        detached: { desktop: detachedMetrics, mobile: detachedMetricsMobile } = {},
        offset: { value: metricsOffsetValue, unit: metricsOffsetUnit } = {},
        gap: { desktop: metricsGapValueDesktop, mobile: metricsGapValueMobile } = {},
        borderRadius: detachedMetricsBorderRadius,
    } = useMetricsConfig();

    const heroVisible = useDesignStore((state) => state.heroVisible);
    const location = useLocation();
    const isHomepage = location.pathname === '/';

    const heroVariant = resolveHeroVariant({ isHomepage, hasHero, heroVisible });
    const isHeroHidden = heroVariant === HERO_VARIANT.HIDDEN;
    const useStandardHero = heroVariant === HERO_VARIANT.STANDARD;

    const heroFullWidth = useStandardHero ? isStandardKeyVisualFullWidth : isKeyVisualFullWidth;

    const isFocusDesktop = Boolean(isFocusModeDesktop);
    const isFocusMobile = Boolean(isFocusModeMobile);

    const effectiveDetachedMetrics = isFocusDesktop || isHeroHidden ? false : detachedMetrics || false;
    const effectiveDetachedMetricsMobile = isFocusMobile || isHeroHidden ? false : detachedMetricsMobile || false;

    const metricsPlacement = resolveMetricsPlacement({
        pageNotFound: Boolean(pageNotFound),
        detachedDesktop: effectiveDetachedMetrics,
        detachedMobile: effectiveDetachedMetricsMobile,
        heroHidden: isHeroHidden,
    });

    const { metricsRef, metricsHeight } = useMetricsNodeHeight();

    const getDetachedMarginBottom = (gapValue) => {
        const gapStr = formatCSSValue(gapValue) || '0px';
        const offsetVal = Number(metricsOffsetValue);

        if (isNaN(offsetVal)) {
            return `max(0px, calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapStr}))`;
        }

        if (metricsOffsetUnit === 'px') {
            const netHeight = metricsHeight + offsetVal;
            return `max(0px, calc(${netHeight}px + ${gapStr}))`;
        }

        if (metricsOffsetUnit === '%') {
            const netHeight = metricsHeight * (1 + offsetVal / 100);
            return `max(0px, calc(${netHeight}px + ${gapStr}))`;
        }

        const offsetSign = offsetVal < 0 ? '-' : '+';
        const absOffset = Math.abs(offsetVal);
        return `max(0px, calc(${metricsHeight}px ${offsetSign} ${absOffset}${metricsOffsetUnit} + ${gapStr}))`;
    };

    useEffect(() => {
        const gapDesktopStr = formatCSSValue(metricsGapValueDesktop) || '0px';
        const gapMobileStr = formatCSSValue(metricsGapValueMobile) || '0px';

        if (effectiveDetachedMetrics) {
            document.documentElement.style.setProperty(
                '--metrics-margin-top-desktop',
                `calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapDesktopStr})`
            );
        } else {
            document.documentElement.style.removeProperty('--metrics-margin-top-desktop');
        }

        if (effectiveDetachedMetricsMobile) {
            document.documentElement.style.setProperty(
                '--metrics-margin-top-mobile',
                `calc(${metricsHeight}px + ${metricsOffsetValue}${metricsOffsetUnit} + ${gapMobileStr})`
            );
        } else {
            document.documentElement.style.removeProperty('--metrics-margin-top-mobile');
        }

        return () => {
            document.documentElement.style.removeProperty('--metrics-margin-top-desktop');
            document.documentElement.style.removeProperty('--metrics-margin-top-mobile');
        };
    }, [
        metricsHeight,
        effectiveDetachedMetrics,
        effectiveDetachedMetricsMobile,
        metricsOffsetValue,
        metricsOffsetUnit,
        metricsGapValueDesktop,
        metricsGapValueMobile,
    ]);

    const inflowMetrics = (
        <div
            className={`${heroFullWidth ? 'container-fluid px-0' : 'container-lg px-0'} ${styles.inflowMetricsWrapper}`}
            style={{
                ...(metricsGapValueDesktop !== undefined
                    ? { '--inflow-margin-bottom-desktop': formatCSSValue(metricsGapValueDesktop) }
                    : {}),
                ...(metricsGapValueMobile !== undefined
                    ? { '--inflow-margin-bottom-mobile': formatCSSValue(metricsGapValueMobile) }
                    : {}),
            }}
        >
            <CampaignMetrics />
        </div>
    );

    if (isFocusDesktop && isFocusMobile) {
        return (
            <header className="w-100" style={{ display: 'contents' }}>
                <div className={`w-100 sticky-top ${styles.countdownStickyWrapper}`}>
                    <div className={isCountdownFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>{counter}</div>
                </div>
            </header>
        );
    }

    const navFocusClass = isFocusDesktop ? 'd-lg-none' : isFocusMobile ? 'd-none d-lg-block' : '';

    return (
        <header className="w-100" style={{ display: 'contents' }}>
            <div className={`w-100 sticky-top ${styles.countdownStickyWrapper}`}>
                <div className={isCountdownFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>{counter}</div>
            </div>

            <div className={navFocusClass || undefined}>
                <div style={navBackgroundColor ? { backgroundColor: navBackgroundColor } : undefined}>
                    <div className={isNavFullWidth ? 'container-fluid px-0' : 'container-lg px-0'}>
                        <MainMenu primaryNavigation={primaryNavigation} primaryNavigationCTA={primaryNavigationCTA} />
                    </div>
                </div>

                <div
                    className={`position-relative w-100 ${
                        effectiveDetachedMetrics
                            ? `${styles.headerVisualWrapperOverlay} ${effectiveDetachedMetricsMobile ? styles.detachedMobile : ''}`
                            : ''
                    }`}
                    style={
                        effectiveDetachedMetrics
                            ? {
                                  '--metrics-margin-bottom': getDetachedMarginBottom(metricsGapValueDesktop),
                                  ...(effectiveDetachedMetricsMobile
                                      ? {
                                            '--metrics-margin-bottom-mobile':
                                                getDetachedMarginBottom(metricsGapValueMobile),
                                        }
                                      : {}),
                              }
                            : {}
                    }
                >
                    {/* Background Section (Solid Fill vs KeyVisual Image) */}
                    {!isHeroHidden && (
                        <HeroBackground
                            pageNotFound={pageNotFound}
                            maxOverlayHeight={maxOverlayHeight}
                            variant={heroVariant}
                        />
                    )}

                    {/* Overlaid Elements Grouped by Position */}
                    {!pageNotFound && heroVariant === HERO_VARIANT.HERO && (
                        <HeroOverlay onGroupHeightChange={onGroupHeightChange} padding={heroOverlayPadding} />
                    )}

                    {/* Overlaid Campaign Metrics (Desktop only) */}
                    {!pageNotFound && effectiveDetachedMetrics && (
                        <div
                            ref={metricsRef}
                            className={`position-absolute bottom-0 start-50 w-100 ${
                                effectiveDetachedMetricsMobile ? '' : 'd-none d-lg-block'
                            } ${styles.metricsOverlayContainer}`}
                            style={{
                                transform: `translate(-50%, calc(100% + ${metricsOffsetValue}${metricsOffsetUnit}))`,
                            }}
                        >
                            <div className="container-lg">
                                <div
                                    className={styles.metricsOverlayCard}
                                    style={
                                        detachedMetricsBorderRadius !== undefined
                                            ? {
                                                  '--detached-metrics-border-radius':
                                                      formatCSSValue(detachedMetricsBorderRadius),
                                              }
                                            : {}
                                    }
                                >
                                    <CampaignMetrics />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {effectiveDetachedMetrics && (
                    <div
                        className={styles.detachedSpacer}
                        style={{
                            '--spacer-height-desktop': getDetachedMarginBottom(metricsGapValueDesktop),
                            '--spacer-height-mobile': effectiveDetachedMetricsMobile
                                ? getDetachedMarginBottom(metricsGapValueMobile)
                                : '0px',
                        }}
                    />
                )}

                {metricsPlacement === METRICS_PLACEMENT.INFLOW && inflowMetrics}

                {metricsPlacement === METRICS_PLACEMENT.MOBILE_ONLY && <div className="d-lg-none">{inflowMetrics}</div>}
            </div>
        </header>
    );
};

export default Header;
